import React, { useCallback, useRef } from 'react';
import { UseFormReturnType } from '@mantine/form';
import { MantineWizardFormData } from '../components/tasks/wizard/utils/mantineFormSchema';
import type { InlineRequirement } from '../components/tasks/wizard/types/data/InlineRequirement';
import { Task } from '../types';
import { generateId } from '../utils/helpers';

interface DragResult {
  destination: { index: number } | null;
  source: { index: number } | null;
}

interface UseRequirementsOptions {
  form: UseFormReturnType<MantineWizardFormData>;
  isLoading?: boolean;
  onRequirementAdded?: (requirement: InlineRequirement) => void;
  onRequirementRemoved?: (index: number) => void;
  onRequirementUpdated?: (index: number, text: string) => void;
  onRequirementsReordered?: (requirements: InlineRequirement[]) => void;
}

interface UseRequirementsReturn {
  // State
  displayRequirements: InlineRequirement[];
  totalCount: number;

  // Actions
  addRequirement: (text: string) => void;
  removeRequirement: (index: number) => void;
  updateRequirement: (index: number, text: string) => void;
  handleDragEnd: (result: DragResult) => void;
  handleKeyPress: (event: React.KeyboardEvent<HTMLInputElement>, text: string, onAddRequirement: () => void) => void;

  // Utilities
  getRequirementKey: (requirement: Task['requirements'][0] | InlineRequirement) => string;
  isInlineRequirement: (req: Task['requirements'][0] | InlineRequirement) => req is InlineRequirement;
  scrollToBottom: () => void;

  // Refs
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Custom hook for managing requirements in the task wizard
 * Extracts all business logic related to requirement management from Step3Requirements component
 *
 * Features:
 * - Add/remove/update requirements
 * - Drag and drop reordering with validation
 * - Form state synchronization
 * - Keyboard interaction handling
 * - Auto-scrolling to newly added requirements
 * - Type safety with proper error handling
 * - React 19+ optimizations with proper memoization
 */
export const useRequirements = ({
  form,
  isLoading = false,
  onRequirementAdded,
  onRequirementRemoved,
  onRequirementUpdated,
  onRequirementsReordered,
}: UseRequirementsOptions): UseRequirementsReturn => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Type guard for InlineRequirement
  const isInlineRequirement = useCallback((req: Task['requirements'][0] | InlineRequirement): req is InlineRequirement => {
    return 'tempId' in req;
  }, []);

  // Helper function to get stable ID for draggable components
  const getRequirementKey = useCallback((requirement: Task['requirements'][0] | InlineRequirement): string => {
    return requirement.id || (isInlineRequirement(requirement) ? requirement.tempId : '') || '';
  }, [isInlineRequirement]);

  // Scroll to bottom utility
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          top: scrollContainerRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  }, []);

  // Add requirement logic
  const addRequirement = useCallback((text: string) => {
    if (!text.trim() || isLoading) return;

    const currentRequirements = form.values.requirements;
    const newRequirement: InlineRequirement = {
      id: generateId(),
      tempId: generateId(),
      text: text.trim(),
      order: currentRequirements.length,
      completed: false,
    };

    const updatedRequirements = [...currentRequirements, newRequirement];
    form.setFieldValue('requirements', updatedRequirements);

    // Call optional callback
    onRequirementAdded?.(newRequirement);

    // Auto-scroll to show the newly added requirement
    scrollToBottom();
  }, [form, isLoading, onRequirementAdded, scrollToBottom]);

  // Remove requirement logic
  const removeRequirement = useCallback((index: number) => {
    if (isLoading) return;

    const currentRequirements = form.values.requirements;

    // Validate index
    if (index < 0 || index >= currentRequirements.length) {
      console.warn('Invalid requirement index for removal:', index);
      return;
    }

    const updatedRequirements = currentRequirements
      .filter((_, i) => i !== index)
      .map((req, i) => ({ ...req, order: i })); // Re-index order

    form.setFieldValue('requirements', updatedRequirements);

    // Call optional callback
    onRequirementRemoved?.(index);
  }, [form, isLoading, onRequirementRemoved]);

  // Update requirement logic
  const updateRequirement = useCallback((index: number, text: string) => {
    if (isLoading) return;

    const currentRequirements = [...form.values.requirements];

    // Validate index and requirement existence
    if (index < 0 || index >= currentRequirements.length) {
      console.warn('Invalid requirement index for update:', index);
      return;
    }

    const requirement = currentRequirements[index];
    if (!requirement) {
      console.warn('Requirement not found at index:', index);
      return;
    }

    requirement.text = text;
    form.setFieldValue('requirements', currentRequirements);

    // Call optional callback
    onRequirementUpdated?.(index, text);
  }, [form, isLoading, onRequirementUpdated]);

  // Drag and drop handling logic
  const handleDragEnd = useCallback((result: DragResult) => {
    if (isLoading) return;

    // Validate drag result
    if (!result.destination || !result.source) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    // No change if dropped at same position
    if (sourceIndex === destinationIndex) {
      return;
    }

    const currentRequirements = [...form.values.requirements];

    // Validate indices
    if (
      sourceIndex < 0 ||
      sourceIndex >= currentRequirements.length ||
      destinationIndex < 0 ||
      destinationIndex >= currentRequirements.length
    ) {
      console.warn('Invalid drag indices:', { sourceIndex, destinationIndex, total: currentRequirements.length });
      return;
    }

    // Perform array reordering
    const removedItems = currentRequirements.splice(sourceIndex, 1);
    const reorderedItem = removedItems[0];

    if (!reorderedItem) {
      console.warn('No item found at source index:', sourceIndex);
      return;
    }

    currentRequirements.splice(destinationIndex, 0, reorderedItem);

    // Update order property for all items
    const updatedRequirements = currentRequirements.map((req, index) => ({
      ...req,
      order: index,
    }));

    form.setFieldValue('requirements', updatedRequirements);

    // Call optional callback
    onRequirementsReordered?.(updatedRequirements);
  }, [form, isLoading, onRequirementsReordered]);

  // Keyboard interaction handling
  const handleKeyPress = useCallback((
    event: React.KeyboardEvent<HTMLInputElement>,
    text: string,
    onAddRequirement: () => void
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (text.trim() && !isLoading) {
        onAddRequirement();
      }
    }
  }, [isLoading]);

  // Get current requirements for display
  const displayRequirements = form.values.requirements;
  const totalCount = displayRequirements.length;

  return {
    // State
    displayRequirements,
    totalCount,

    // Actions
    addRequirement,
    removeRequirement,
    updateRequirement,
    handleDragEnd,
    handleKeyPress,

    // Utilities
    getRequirementKey,
    isInlineRequirement,
    scrollToBottom,

    // Refs
    scrollContainerRef,
  };
};